/* Shop the look — the two custom elements the section is built from.

   <macaje-reels>  the coverflow, a subclass of theme.js's generic
                   <reel-carousel> that changes only how many reels stay on
                   screen, how the wrap is hidden, a small gap either side of
                   the centre reel on desktop, when sound comes on, and closes
                   any open card as a reel leaves the centre. Which reel PLAYS
                   is the base class's own rule - centre only.
   <stl-card>      the product card. One product is a row that links straight to
                   its product page; a reel carrying more than one grows a
                   chevron that opens the rest as a list underneath. Reels with a
                   single product render no chevron at all, so there is nothing
                   for this to do beyond leaving the one row alone.

   The card is pinned to the bottom of its reel (see the stylesheet), so opening
   it pushes its top edge up over the foot of the video rather than downward into
   .reels' clip. The height animation is CSS — 0fr to 1fr on a grid row — so
   nothing here measures or sets a pixel height.

   Loaded from sections/shop-the-look.liquid, which renders on both the homepage
   and the product page — hence the define guards: two <script src> tags for the
   same file would otherwise throw on the second define. */

/* --------------------------------------------- deferred reel posters ------ */

/* Give the reels their poster back as the row nears the viewport.

   sections/shop-the-look.liquid ships each <video> with its poster URL parked
   on data-reel-poster (and on data-reel-src for the <img> fallback video_tag
   emits alongside it) and preload="none", because a <video> fetches its poster the
   moment it is parsed and there is no lazy equivalent for it. That was 412KB
   arriving before DOMContentLoaded for a row ~2,400px below the fold. This is
   the half that hands it all back.

   600px of margin, not the 150px the PDP ambassador rail uses: that rail is
   deciding when to PLAY, and a late decision costs nothing visible, whereas a
   late poster is a blank reel. 600px buys most of a screen of warning at scroll
   speed and still leaves ~1,800px of slack before it could reach far enough up
   the page to fetch during startup. It also lands well ahead of the base
   carousel's own observer (threshold 0.15), so the poster is always in place
   before the centre reel is allowed to play.

   No IntersectionObserver means restore immediately. A reel with no poster and
   nothing preloaded is a blank box, which is far worse than the download this
   defers — this is the one failure mode that must not be silent.

   Outside the customElements guard below on purpose: the posters are markup,
   not the coverflow, and must come back even where <macaje-reels> never
   upgrades. */
(function () {
  /* THE ATTRIBUTE NAMES ARE NAMESPACED ON PURPOSE — data-reel-poster and
     data-reel-src, never the generic data-poster / data-src.

     Ziad, 2026-09-08: the first cut parked them on data-poster / data-src, and
     after scrolling to the row three of the seven reels (the centre and its two
     neighbours) ended up with poster="null" — the literal string, pointing the
     reel at /null. It was not this file: a setAttribute trap put the write in
     the Instafeed app,

       cdn.nfcube.com/instafeed-*.js  →  IntersectionObserver → setAttribute

     which runs its own lazy-media pass over the WHOLE page, not just its feed,
     claims anything carrying the conventional data-poster / data-src names, and
     writes poster="null" when its own companion attribute is missing. The live
     theme never showed it because the live theme has no such attributes to
     claim. Renaming is the whole fix; nothing else can be relied on, because
     the app is free to change what it scans for.

     Read the URL, clear it, then assign, and never assign a falsy one — cheap
     insurance so a second pass is a no-op rather than a broken reel. preload
     goes last so nothing kicks the resource-selection algorithm before the
     poster is safely on. */
  function wake(row) {
    row.querySelectorAll('video[data-reel-poster]').forEach(function (v) {
      var src = v.getAttribute('data-reel-poster');
      v.removeAttribute('data-reel-poster');
      if (!src) return;
      v.poster = src;

      // Same URL as the poster, so this is a cache hit, not a second download.
      // Restored anyway so the tag ends up byte-for-byte what it always was.
      var fallback = v.querySelector('img[data-reel-src]');
      if (fallback) {
        var fsrc = fallback.getAttribute('data-reel-src');
        fallback.removeAttribute('data-reel-src');
        if (fsrc) fallback.src = fsrc;
      }

      v.preload = 'metadata';
    });
  }

  var io = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          wake(entry.target);
        });
      }, { rootMargin: '600px 0px', threshold: 0 })
    : null;

  function observe() {
    document.querySelectorAll('macaje-reels, reel-carousel').forEach(function (row) {
      if (row.__reelPosters) return;
      row.__reelPosters = true;
      if (io) io.observe(row); else wake(row);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', observe);
  else observe();
  // The editor re-renders sections wholesale.
  document.addEventListener('shopify:section:load', observe);
})();

(function () {
  if (!window.customElements) return;

  /* ------------------------------------------------- <macaje-reels> ------- */

  /* The coverflow, with this section's ring rules.

     <reel-carousel> in theme.js is the generic component and stays untouched;
     this subclass is what sections/shop-the-look.liquid actually renders, and it
     overrides ONE method. Everything else — wrap-around offsets, scale, videos,
     arrows, keyboard, the IntersectionObserver — is inherited, so a later fix to
     the base carousel still reaches this. assets/reel-swipe.js matches both tags.

     What changes: how many reels stay on screen, and the wrap.

     Only ONE slide ever has to change sides as the row turns — the one at the
     largest offset. Keep that one hidden and every ring nearer the centre can be
     shown and simply cropped by .reels' overflow, which is what fills the edges
     instead of leaving dead space beside the five full reels.

     The floor of 3 means a short row behaves exactly as it always has: five
     across, third ring hidden. There is no spare slot until 8 reels, and without
     one the edge reel would visibly pop in and out on every step. From 8 up the
     third ring turns on by itself and a partial reel sits at each edge — roughly
     a quarter of one on a 1440px screen. The ceiling of 4 keeps it at seven slots
     however long the row gets, rather than painting reels cropped fully away. */
  var Reel = customElements.get('reel-carousel');

  if (Reel && !customElements.get('macaje-reels')) {
    customElements.define(
      'macaje-reels',
      class extends Reel {
        layout() {
          // Positions, scale, aria, tab order and video sync all come from the
          // base class; only the visibility tiers and the seam are re-decided.
          super.layout();

          var n = this.items.length;
          var wrap = Math.min(4, Math.max(3, Math.floor(n / 2)));

          /* A hairline of background either side of the centre reel.

             The centre reel is scaled 1.12 and its neighbours 0.86, but the row
             steps by a flat 236px. Half of 262*1.12 plus half of 262*0.86 comes
             to 259 against that 236 step, so the centre reel currently OVERLAPS
             each neighbour by about 23px - it is painted on top (z 30), which is
             why it reads as touching. A small nudge does nothing here; the shift
             has to clear the overlap before any background can show.

             So it is measured rather than guessed: take the real rendered
             half-widths and the real step off the DOM, work out the overlap, and
             shift by overlap + GAP. If the scales, the step or the tile width are
             ever retuned this recomputes itself instead of silently going wrong
             again.

             The whole NON-centre run moves, not just the two neighbours: moving
             only ±1 would open the centre gap by stealing the same distance from
             the gap between ±1 and ±2. Moving the run together leaves every other
             gap in the row untouched. Nothing is resized or rescaled - this only
             translates, and the centre reel itself never moves.

             Desktop only. this.mq is theme.js's own (max-width: 860px), the same
             breakpoint shop-the-look.css uses, so below 861px shift stays 0 and
             not a single value changes. */
          var GAP = 6;
          var shift = 0;

          if (!this.mq.matches) {
            var W = this.items[0].offsetWidth;
            var scC = 0;
            var scN = 0;
            var step = 0;

            this.items.forEach(function (it, k) {
              var oo = (((k - this.active) % n) + n) % n;
              if (oo > n / 2) oo -= n;
              var sc = parseFloat(it.style.getPropertyValue('--sc')) || 1;
              if (oo === 0) scC = sc;
              if (Math.abs(oo) === 1) {
                scN = sc;
                step = Math.abs(parseFloat(it.style.getPropertyValue('--tx')) || 0);
              }
            }, this);

            if (W && scC && scN && step) {
              // Positive when the two boxes intrude on each other.
              var overlap = (W * scC) / 2 + (W * scN) / 2 - step;
              // Already apart by GAP or more? Then leave it completely alone.
              shift = Math.max(0, Math.round(overlap + GAP));
            }
          }

          this.items.forEach(function (item, k) {
            var o = (((k - this.active) % n) + n) % n;
            if (o > n / 2) o -= n;
            var ao = Math.abs(o);

            item.style.setProperty('--op', ao === 0 ? 1 : ao >= wrap ? 0 : ao === 1 ? 0.92 : 0.55);
            item.style.setProperty('--z', ao === 0 ? 30 : ao >= wrap ? 0 : ao === 1 ? 20 : 10);

            // Push the outer run out by `shift` (computed above) so real
            // background shows either side of the centre reel.
            if (shift && o !== 0) {
              var tx = parseFloat(item.style.getPropertyValue('--tx')) || 0;
              item.style.setProperty('--tx', (tx + (o > 0 ? shift : -shift)) + 'px');
            }

            // The slide crossing the seam travels the whole width of the row in
            // one step. Drop its transition for that step only, so it relocates
            // instantly instead of flying across the screen (base.css already
            // had [data-seam='true'] { transition: none } — nothing set it).
            // Tapping a side reel can legitimately move a slide several places,
            // so the test is against half the row: only a real wrap exceeds it.
            item.dataset.seam = String(item.__o !== undefined && Math.abs(o - item.__o) > n / 2);
            item.__o = o;

            // An open card is a sheet lying over its reel; it has no business
            // staying open once that reel is no longer the one being looked at.
            if (ao !== 0) {
              var card = item.querySelector('stl-card');
              if (card && card.close) card.close();
            }
          }, this);
        }

        /* Sound follows the visitor, not the page load.

           Playback itself is the base class's job and is deliberately NOT
           overridden: <reel-carousel> plays `k === this.active` and pauses the
           rest, which is exactly the wanted behaviour - the centre reel plays,
           everything else stays still, and playback moves with the centre as the
           row turns. Nothing here re-implements that.

           The one thing added is sound. A page cannot start audio on its own -
           every browser refuses it - so the first centre reel autoplays muted.
           The moment the visitor moves the carousel, that IS the user gesture
           the browser was waiting for, so sound is switched on from then on and
           every subsequent centre reel plays with it.

           set() is the right hook because it is only ever called by a real
           navigation - arrows, a tap on a side reel, a swipe via reel-swipe.js.
           The boot path calls layout() directly, and the resize and visibility
           handlers call layout()/syncVideos(), so none of them come through here
           and none of them can turn sound on by themselves.

           `muted` is lowered BEFORE super.set() rather than after, so the single
           layout/syncVideos pass the base class already runs applies it - no
           second pass, and no audible gap on the reel being moved to.

           userMuteChoice is the override: once the visitor has touched the sound
           button, the carousel stops making this decision for them, in either
           direction. Muting stays muted. */
        set(k) {
          if (!this.userMuteChoice) this.muted = false;
          super.set(k);
        }

        toggleMute() {
          // From here on the sound state belongs to the visitor.
          this.userMuteChoice = true;
          super.toggleMute();
        }
      }
    );
  }

  /* ----------------------------------------------------- <stl-card> ------- */

  if (customElements.get('stl-card')) return;

  customElements.define(
    'stl-card',
    class extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-stl-toggle]');
        this.more = this.querySelector('[data-stl-more]');
        if (!this.toggle || !this.more) return;

        // The reel's media is click-to-centre and a drag ends in a click, so the
        // chevron has to keep its press to itself.
        this.toggle.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          this.set(this.dataset.open !== 'true');
        }.bind(this));

        this.set(false);
      }

      open() { this.set(true); }
      close() { this.set(false); }

      set(open) {
        if (!this.toggle) return;
        this.dataset.open = String(open);
        this.toggle.setAttribute('aria-expanded', String(open));
        // inert rather than tabindex: <reel-carousel> rewrites tabIndex on every
        // a/button inside a reel each time the coverflow moves, which would hand
        // focus to the collapsed rows. It does not touch inert.
        this.more.inert = !open;
      }
    }
  );
})();
